import { Mutex } from 'async-mutex';
import { type PromiseResponse, SupportedServerTypes } from 'atp-api-logger';

import { settings } from '@/_config';
import {
  getCookie,
  googleAnalytics4,
  isInstructorAccount,
  isLoggedIn,
  logger,
  removeAuthTokens,
  setCookie,
} from '@/_shared';
import type { BaseQueryFn, FetchArgs } from '@/_state/apis/tanstack-api';
import { fetchBaseQuery } from '@/_state/apis/tanstack-api';

// Create a new mutex
const mutex = new Mutex();

const baseQueryWithReAuth = (
  { baseUrl, appName, authRequired }: { baseUrl: string; appName: string; authRequired?: boolean } = {
    baseUrl: '',
    appName: 'ATP',
    authRequired: true,
  }
): BaseQueryFn => {
  const baseQuery = fetchBaseQuery({
    baseUrl,
    timeout: 600000,
    prepareHeaders: (headers) => {
      if (authRequired) {
        const token = getCookie('access');
        if (token) {
          headers.set('authorization', `Bearer ${token}`);
        }
      }

      if (appName === 'INSTRUCTOR') {
        headers.set('gopon', `raaz`);
      }
      return headers;
    },
  });

  const mainQueryFunc: BaseQueryFn = async (args, api, extraOptions) => {
    // wait until the mutex is available without locking it
    await mutex.waitForUnlock();
    let label = appName;
    if (label === 'ANSWER_ON_DEMAND') {
      if (isLoggedIn()) {
        label = isInstructorAccount() ? label + '_INSTRUCTOR' : label + '_ATP';
      } else {
        label = 'CHECKOUT';
      }
    }

    logAll(api.endpoint, label);

    const promise = baseQuery(args, api, extraOptions);
    // Log the api call
    const api_name = typeof args === 'string' ? `${args.split('?')[0]}` : `${args.url.split('?')[0]}`;
    const method = typeof args === 'string' ? 'GET' : args.method || 'GET';

    let server_type = SupportedServerTypes.PREP;
    switch (baseUrl) {
      case settings.baseURL[0]:
        server_type = SupportedServerTypes.PREP;
        break;
      case settings.baseURL[1]:
        server_type = SupportedServerTypes.AOD;
        break;
      case settings.baseURL[2]:
        server_type = SupportedServerTypes.INSTRUCTOR;
        break;
    }

    logger.trackPromise(promise as Promise<PromiseResponse>, {
      api_name,
      method,
      server_type,
    });

    let result = await promise;

    if (
      authRequired &&
      result.error &&
      result.error.status === 401 &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (result?.error?.data as any)?.code === 'token_not_valid'
    ) {
      if (!mutex.isLocked()) {
        const release = await mutex.acquire();

        try {
          const resetToken: string = getCookie('refresh');

          // if no refresh token, we can't refresh
          if (!resetToken) {
            removeAuthTokens();
            window.location.reload();
            return result;
          }

          logAll('/token/refresh/', label);
          const refreshResult = await baseQuery(
            {
              // TODO better logic for refreshing token address
              // crashes on aod server
              url: (isInstructorAccount() ? settings.baseURL[2] : settings.baseURL[0]) + '/token/refresh/',
              method: 'POST',
              body: {
                refresh: resetToken,
              },
            } as FetchArgs,
            api,
            extraOptions
          );

          if (refreshResult?.data) {
            const data = refreshResult.data as Record<string, unknown>;
            if (typeof data?.access === 'string') {
              setCookie('access', data.access);
            }

            // Retry the initial query
            logAll(api.endpoint, label);
            result = await baseQuery(args, api, extraOptions);
          } else {
            removeAuthTokens();
            window.location.reload();
          }
        } finally {
          // release must be called once the mutex should be released again.
          release();
        }
      } else {
        // wait until the mutex is available without locking it
        await mutex.waitForUnlock();
        logAll(api.endpoint, label);
        result = await baseQuery(args, api, extraOptions);
      }
    }

    return result;
  };

  return mainQueryFunc;
};

export default baseQueryWithReAuth;

function logAll(endpoint: string, label: string) {
  try {
    googleAnalytics4.event({
      category: 'API_CALL_LOG',
      action: endpoint,
      label,
      nonInteraction: true,
    });
  } catch (e) {
    console.log(e);
  }
}
